import React, { useState } from "react";
import styled from "styled-components";
import { X } from "lucide-react";

interface TermsConsentProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  disabled?: boolean;
}

interface TermsModalProps {
  show: boolean;
  onClose: () => void;
  children: React.ReactNode;
}

const TermsModal: React.FC<TermsModalProps> = ({ show, onClose, children }) => {
  if (!show) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={(e) => e.stopPropagation()}>
        <CloseButton onClick={onClose}>
          <X size={20} />
        </CloseButton>
        {children}
      </ModalContent>
    </ModalOverlay>
  );
};

const TermsConsent: React.FC<TermsConsentProps> = ({
  checked,
  onChange,
  disabled = false,
}) => {
  const [showTerms, setShowTerms] = useState(false);

  return (
    <TermsConsentContainer>
      <TermsModal show={showTerms} onClose={() => setShowTerms(false)}>
        <TermsContent>
          <h1>FiPay.io Pay by Bank Terms and Conditions</h1>

          <p>
            These Terms and Conditions govern your use of the FiPay.io Pay by
            Bank service ("Service") provided by FiPay.io ("we," "us," or
            "our"). By using the Service, you ("you" or "user") agree to be
            bound by these Terms and Conditions. Please read them carefully
            before using the Service.
          </p>

          <Section>
            <h2>1. Service Description</h2>
            <p>
              The FiPay.io Pay by Bank service allows you to connect your bank
              account to our partner financial institution(s) ("Partner") to
              facilitate payments for goods and services offered through
              merchants integrated with FiPay.io. This involves authorizing us
              to debit your linked bank account for transactions you initiate
              and to store your bank account information securely.
            </p>
          </Section>

          <Section>
            <h2>2. Authorization to Debit and Store Information</h2>
            <p>
              By connecting your bank account to the Service, you grant us and
              our Partner the following authorizations:
            </p>
            <p>
              <strong>Debit Authorization:</strong> You authorize us and our
              Partner to initiate electronic debits from your linked bank
              account for the amounts you authorize when making payments through
              the Service. This includes one-time payments and recurring
              payments (if applicable).
            </p>
            <p>
              <strong>Information Storage:</strong> You authorize us to securely
              store your bank account information, including your bank name,
              account number, and routing number, for the purpose of processing
              payments and providing the Service. We will only use this
              information as described in these Terms and our Privacy Policy.
            </p>
          </Section>

          <Section>
            <h2>3. User Responsibilities</h2>
            <p>
              <strong>Accuracy of Information:</strong> You are responsible for
              providing accurate and complete bank account information when
              connecting your account to the Service. Any errors in the
              information you provide may result in payment delays or failures.
            </p>
            <p>
              <strong>Sufficient Funds:</strong> You are responsible for
              ensuring that sufficient funds are available in your linked bank
              account to cover the amounts of the payments you authorize. We are
              not responsible for any overdraft fees or other charges incurred
              by your bank due to insufficient funds.
            </p>
            <p>
              <strong>Security:</strong> You are responsible for maintaining the
              security of your FiPay.io account credentials and your bank
              account information. Do not share your login details with anyone.
              Notify us immediately if you suspect any unauthorized access to
              your account.
            </p>
          </Section>

          <Section>
            <h2>4. Fees</h2>
            <p>
              [Clearly state any fees associated with using the Service. If
              there are no fees, state "There are currently no fees associated
              with the FiPay.io Pay by Bank service." Be transparent about any
              potential future fees.]
            </p>
          </Section>

          <Section>
            <h2>5. Dispute Resolution</h2>
            <p>
              If you have any questions or disputes regarding a payment made
              through the Service, please contact our support team [Your Support
              Email Address or Contact Information]. We will work with you and
              our Partner to resolve the issue.
            </p>
          </Section>

          <Section>
            <h2>6. Termination</h2>
            <p>
              You may opt-out of the Service and revoke our authorization to
              debit your bank account and store your information at any time by
              contacting us. Upon termination, we will cease initiating debits
              from your bank account and will securely delete your stored bank
              account information, subject to applicable legal requirements.
              Please note that termination of the service will not affect any
              payments that have already been processed.
            </p>
          </Section>

          <Section>
            <h2>7. Privacy Policy</h2>
            <p>
              Your privacy is important to us. Our Privacy Policy explains how
              we collect, use, and protect your personal information. By using
              the Service, you agree to the terms of our Privacy Policy.
            </p>
          </Section>

          <Section>
            <h2>8. Modifications to Terms and Conditions</h2>
            <p>
              We may update these Terms and Conditions from time to time. We
              will notify you of any material changes by email or in-app
              notification. Your continued use of the Service after the
              effective date of the revised Terms and Conditions constitutes
              your acceptance of the changes.
            </p>
          </Section>

          <Section>
            <h2>9. Governing Law</h2>
            <p>
              These Terms and Conditions shall be governed by and construed in
              accordance with the applicable laws of your State / Jurisdiction.
            </p>
          </Section>

          <Section>
            <h2>10. Contact Information</h2>
            <ContactInfo>
              <p>
                If you have any questions about these Terms and Conditions,
                please contact us at:
              </p>
              <p>
                <FiPayLink
                  href="https://fipay.io"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  FiPay.io
                </FiPayLink>
                <br />
                338 main street, San Francisco, California, 94105
                <br />
                <FiPayLink href="mailto:info@fipay.io">info@fipay.io</FiPayLink>
              </p>
            </ContactInfo>
          </Section>
        </TermsContent>
      </TermsModal>

      <CheckboxContainer>
        <CheckboxWrapper onClick={() => !disabled && onChange(!checked)}>
          <HiddenCheckbox type="checkbox" checked={checked} readOnly />
          <StyledCheckbox checked={checked}>
            {checked && <CheckMark>✓</CheckMark>}
          </StyledCheckbox>
        </CheckboxWrapper>
        <CheckboxLabel>
          By clicking Next, you agree to our{" "}
          <TermsLink onClick={() => setShowTerms(true)}>
            <strong>Terms and Conditions.</strong>
          </TermsLink>
        </CheckboxLabel>
      </CheckboxContainer>
    </TermsConsentContainer>
  );
};

export default TermsConsent;

// Styled Components
const TermsConsentContainer = styled.div`
  margin: 0.5rem 0;
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 8px;
  max-width: 800px;
  max-height: 80vh;
  width: 90%;
  margin: 20px;
  overflow: hidden;
  position: relative;
`;

const TermsContent = styled.div`
  padding: 2rem;
  overflow-y: auto;
  max-height: calc(80vh - 4rem);

  h1 {
    color: #2c3e50;
    text-align: left;
    border-bottom: 2px solid #eee;
    padding-bottom: 10px;
    margin-bottom: 20px;
  }

  h2 {
    color: #34495e;
    margin-top: 25px;
    font-size: 1.2rem;
  }

  p {
    line-height: 1.6;
    color: #333;
    margin-bottom: 1rem;
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const ContactInfo = styled.div`
  background-color: #f8f9fa;
  padding: 20px;
  border-radius: 5px;
  margin-top: 20px;
`;

const CloseButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  background: #00a603;
  border: none;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  color: white;
  padding: 0;
  transition: background-color 0.2s ease;

  &:hover {
    background: #008802;
  }
`;

const FiPayLink = styled.a`
  color: #00a603;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

const CheckboxContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const CheckboxWrapper = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  cursor: pointer;
`;

const HiddenCheckbox = styled.input`
  position: absolute;
  opacity: 0;
  height: 0;
  width: 0;
`;

const StyledCheckbox = styled.div<{ checked: boolean }>`
  width: 18px;
  height: 18px;
  border: 1px solid ${(props) => (props.checked ? "#00a603" : "#3f3f3f")};
  background: ${(props) => (props.checked ? "#00a603" : "white")};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  border-radius: 4px;
`;

const CheckMark = styled.span`
  color: white;
  font-size: 12px;
`;

const CheckboxLabel = styled.label`
  font-size: 14px;
  color: #363636;
`;

const TermsLink = styled.span`
  color: #00a603;
  text-decoration: underline;
  cursor: pointer;

  &:hover {
    color: #008802;
  }
`;
